import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { cva } from 'class-variance-authority';
import { Icon } from './icons/Icon';
import { IconName } from '../icons/names';
import { cn } from '../utils';

export interface ToastProps {
  message?: string;
  title?: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  variant?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  icon?: IconName;
  onClose?: () => void;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  closeAriaLabel?: string;
  testId?: string;
}

const VARIANT_ICONS: Record<string, IconName> = {
  success: 'circle-check',
  error: 'triangle-exclamation',
  warning: 'triangle-exclamation',
  info: 'circle-info'
};

const POSITION_CLASSES: Record<NonNullable<ToastProps['position']>, string> = {
  'top-left': 'top-4 left-4',
  'top-right': 'top-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'bottom-right': 'bottom-4 right-4'
};

const toastCardVariants = cva('flex items-start gap-sm p-md rounded-lg border shadow-lg', {
  variants: {
    variant: {
      success: 'bg-success-bg text-success-text border-success-border',
      error: 'bg-error-bg text-error-text border-error-border',
      warning: 'bg-warn-bg text-warn-text border-warn-border',
      info: 'bg-info-bg text-info-text border-info-border'
    }
  },
  defaultVariants: { variant: 'info' }
});

const toastIconVariants = cva('shrink-0 mt-2xs', {
  variants: {
    variant: {
      success: 'text-success-border',
      error: 'text-error-border',
      warning: 'text-warn-border',
      info: 'text-info-border'
    }
  },
  defaultVariants: { variant: 'info' }
});

export const Toast: React.FC<ToastProps> = ({
  message,
  title,
  type = 'info',
  variant,
  duration = 8000,
  icon,
  onClose,
  position = 'top-right',
  closeAriaLabel = 'Close',
  testId
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const effectiveVariant = variant || type;
  const displayIcon = icon || VARIANT_ICONS[effectiveVariant] || VARIANT_ICONS.info;

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true));

    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onClose?.(), 300);
    }, duration);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [duration, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  return (
    <div
      role={effectiveVariant === 'error' ? 'alert' : 'status'}
      aria-live={effectiveVariant === 'error' ? 'assertive' : 'polite'}
      aria-atomic='true'
      data-testid={testId}
      data-toast-type={effectiveVariant}
      style={{ zIndex: 9999 }}
      className={cn(
        'fixed z-[9999]! max-w-[448px] transition-[opacity,transform] duration-300 pointer-events-auto',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2',
        POSITION_CLASSES[position]
      )}
    >
      <div className={toastCardVariants({ variant: effectiveVariant })}>
        <Icon name={displayIcon} className={toastIconVariants({ variant: effectiveVariant })} />
        <div className='flex-1'>
          {title && message && <div className='font-semibold mb-xs'>{title}</div>}
          <div className='text-sm font-medium'>{message || title}</div>
        </div>
        {onClose && (
          <button
            onClick={handleClose}
            className='shrink-0 bg-transparent border-0 p-0 cursor-pointer'
            aria-label={closeAriaLabel}
          >
            <Icon name='xmark' />
          </button>
        )}
      </div>
    </div>
  );
};

class ToastManager {
  private container: HTMLElement | null = null;
  private toasts: Map<string, { root: ReactDOM.Root; wrapper: HTMLElement }> = new Map();

  private getContainer(): HTMLElement {
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'toast-container';
      this.container.style.cssText =
        'position: fixed; top: 0; left: 0; width: 0; height: 0; z-index: 9999; pointer-events: none; overflow: visible;';
      document.body.appendChild(this.container);
    }
    return this.container;
  }

  show(props: ToastProps): void {
    const container = this.getContainer();
    const toastId = `toast-${performance.now()}-${Math.random()}`;
    const toastWrapper = document.createElement('div');
    toastWrapper.id = toastId;
    toastWrapper.style.cssText = 'pointer-events: none;';

    container.appendChild(toastWrapper);

    const root = ReactDOM.createRoot(toastWrapper);

    const handleClose = () => {
      root.unmount();
      toastWrapper.remove();
      this.toasts.delete(toastId);
      props.onClose?.();
    };

    root.render(
      React.createElement(Toast, {
        ...props,
        onClose: handleClose
      })
    );

    this.toasts.set(toastId, { root, wrapper: toastWrapper });
  }

  /** Unmounts every toast without calling its `onClose`; each toast is a React root of its own. */
  clear(): void {
    for (const { root, wrapper } of this.toasts.values()) {
      root.unmount();
      wrapper.remove();
    }
    this.toasts.clear();
    this.container?.remove();
    this.container = null;
  }
}

const toastManager = new ToastManager();

export const showToast = (props: ToastProps) => {
  toastManager.show(props);
};

/**
 * Removes every toast at once. A test harness's own cleanup never reaches them, so their
 * dismiss timers would otherwise outlive the test environment.
 */
export const clearToasts = () => {
  toastManager.clear();
};

export const useToast = () => {
  return {
    show: showToast,
    showToast,
    toast: showToast
  };
};
